import {
  DashboardState,
  EAppSupportType,
  ECardinality,
  EProjectType,
  ESdChangeReason,
  ESymbolCategory,
  IAction,
  IActionDefinitionDto,
  IActionParameter,
  IForEachDataModel,
  ISymbol
} from "@kuika/kuika-cl-model";
import type { CascaderOptionType, CascaderValueType } from "antd/es/cascader";
import { Guid } from "guid-typescript";
import _ from "lodash";
import React from "react";
import { DragDropContext, Draggable, Droppable } from "react-beautiful-dnd";
import { IWithSDProps, withSD } from "../../../../shared/hoc/screen-design-consumer";
import { PureSdComponent } from "../../../../shared/pure-sd";
import { ActionDefinitionLookup } from "../../../../shared/utility/action-definition-lookup";
import { KMainFunctions } from "../../../../shared/utility/main-functions";
import { isOfflineCompatibleActionDefinition } from "../../../../shared/utility/offline-action-compatibility";
import { ActionPanel, EActionPanelStatus } from "./action-panel";
import ActionParameterTitle from "./action-parameter-title";
import { EActionNodeCategory } from "./action-picker";
import type { IActionPickerOption } from "./action-picker-popup/action-picker-node";
import ActionPickerPopup from "./action-picker-popup/action-picker-popup";
import { SymbolPicker } from "./symbol-picker";

export interface IForEachActionSymbolGroupState {
  popupVisible: boolean;
  /** The popup's option tree, built when it opens. */
  options: IActionPickerOption[];
}

export interface IForEachActionSymbolGroupProps {
  action: IAction;
  forEachData?: IForEachDataModel;
  updateForEachData: (forEachData: IForEachDataModel, changeReason?: ESdChangeReason) => void;
  getActionDefinitionItems: () => CascaderOptionType[];
}

class ForEachActionSymbolGroup extends PureSdComponent<
  IForEachActionSymbolGroupProps & IWithSDProps,
  IForEachActionSymbolGroupState
> {
  constructor(props: IForEachActionSymbolGroupProps & IWithSDProps) {
    super(props);
    this.state = {
      popupVisible: false,
      options: []
    };
  }

  /** Opens and closes the popup. The tree is built on open, so it lists the current catalogue. */
  handlePopupOpenChange = (open: boolean): void => {
    if (open) {
      this.setState({ options: this.props.getActionDefinitionItems(), popupVisible: true });
    } else if (this.state.popupVisible) {
      this.setState({ popupVisible: false });
    }
  };

  /** The popup's pick: the same `selectedOptions` path the Cascader's onChange delivered. */
  handlePick = (selectedOptions: CascaderOptionType[]): void => {
    const values: CascaderValueType = selectedOptions.map((option: CascaderOptionType) => option.value ?? "");
    this.handleAddIfAction(values, selectedOptions);
  };

  actionDefinitionLookup: ActionDefinitionLookup = new ActionDefinitionLookup();

  getActionDefinitionById = (id: string): IActionDefinitionDto | undefined =>
    this.actionDefinitionLookup.get(this.props.sd.actionDefinitions, id);

  shouldActionsBeVisible = (): boolean => {
    // if (_.isUndefined(this.props.ifThenElseData?.condition?.field1)) return false;
    // if (
    //   _.isUndefined(this.props.ifThenElseData?.condition?.field2) &&
    //   this.props.ifThenElseData?.condition?.conditionOperator != "isEmpty" &&
    //   this.props.ifThenElseData?.condition?.conditionOperator != "isNotEmpty" &&
    //   this.props.ifThenElseData?.condition?.conditionOperator != "isGuid"
    // )
    //   return false;
    // if (_.isEmpty(this.props.ifThenElseData?.condition.field1)) return false;
    // if (
    //   _.isEmpty(this.props.ifThenElseData?.condition.field2) &&
    //   this.props.ifThenElseData?.condition?.conditionOperator != "isEmpty" &&
    //   this.props.ifThenElseData?.condition?.conditionOperator != "isNotEmpty" &&
    //   this.props.ifThenElseData?.condition?.conditionOperator != "isGuid"
    // )
    //   return false;

    return true;
  };

  handleAdd = (
    additionType: "if" | "else",
    value: CascaderValueType,
    selectedOptions?: CascaderOptionType[] | undefined
  ) => {
    if (!selectedOptions || selectedOptions.length == 0) {
      return;
    }
    const node = selectedOptions.find(
      (o) => o.category === EActionNodeCategory.action || o.category === EActionNodeCategory.triggerEventAction
    );

    if (node === undefined) {
      return;
    }
    let actions = this.props.sd.actionDefinitions;
    const isWebScreen = this.props.sd.getIsWebScreen();
    actions = actions.filter((x) => {
      const { projectType } = this.props.sd.project;
      if (
        x.appSupportType &&
        x.appSupportType === EAppSupportType.Web &&
        projectType !== EProjectType.Desktop &&
        this.props.sd.dashboardState !== DashboardState.scheduledJobs &&
        !isWebScreen
      ) {
        return false;
      }
      if (
        x.appSupportType &&
        x.appSupportType === EAppSupportType.Mobile &&
        projectType === EProjectType.Desktop &&
        this.props.sd.dashboardState !== DashboardState.scheduledJobs
      ) {
        return false;
      }
      if (this.props.sd.project.offlineScreenId === KMainFunctions.getSdIdFromUrl()) {
        if (isOfflineCompatibleActionDefinition(x)) return true;
        return false;
      }
      return true;
    });
    let action: IAction | undefined;

    switch (node.category) {
      case EActionNodeCategory.action:
        action = this.obtainAction(actions, node);
        break;
      case EActionNodeCategory.triggerEventAction:
        action = this.obtainTriggerAction(actions, selectedOptions);
        break;
    }

    if (!action) return;

    this.createAction(action);
  };

  obtainAction(actions: IActionDefinitionDto[], node: CascaderOptionType): IAction | undefined {
    const actionDef = actions.find((p) => p.id === node.value);
    if (!actionDef) return;

    return {
      id: Guid.create().toString(),
      actionDefinitionId: actionDef.id,
      label: actionDef.name,
      description: actionDef.name,
      parameters: actionDef.actionParameters.map((x) => this.cloneActionParameter(x))
    } as IAction;
  }

  obtainTriggerAction(actions: IActionDefinitionDto[], selectedOptions: CascaderOptionType[]): IAction | undefined {
    const actionDef = actions.find((p) => p.name === "TriggerEvent");
    if (!actionDef) return;

    const targetDiNode = selectedOptions[1];
    const targetEventNode = selectedOptions[2];
    const targetDiIdActionParam = actionDef.actionParameters.find((x) => x.name === "targetDiId");
    const targetEventNameActionParam = actionDef.actionParameters.find((x) => x.name === "targetEventName");

    if (!targetDiIdActionParam || !targetEventNameActionParam) return;

    targetDiIdActionParam.symbol = {
      category: ESymbolCategory.fixed,
      fixedValue: targetDiNode.value
    } as ISymbol;

    targetEventNameActionParam.symbol = {
      category: ESymbolCategory.fixed,
      fixedValue: targetEventNode.value
    } as ISymbol;

    return {
      id: Guid.create().toString(),
      actionDefinitionId: actionDef.id,
      label: actionDef.name,
      description: `Trigger [diId:${targetDiNode.value}] > ${targetEventNode.label}`,
      parameters: [targetDiIdActionParam, targetEventNameActionParam]
    } as IAction;
  }

  handleAddIfAction = (value: CascaderValueType, selectedOptions?: CascaderOptionType[] | undefined) => {
    this.handleAdd("if", value, selectedOptions);
  };

  handleAddElseAction = (value: CascaderValueType, selectedOptions?: CascaderOptionType[] | undefined) => {
    this.handleAdd("else", value, selectedOptions);
  };

  cloneActionParameter = (actionParameter: IActionParameter) => {
    const tmp = _.cloneDeep(actionParameter);
    return tmp;
  };

  getEmptyForEachData = (): IForEachDataModel => {
    return {
      actions: [],
      symbolModel: {
        category: ESymbolCategory.actionResults,
        label: "",
        description: ""
      }
    };
  };

  createAction = (action: IAction) => {
    let forEachData = _.cloneDeep(this.props.forEachData);
    if (forEachData === undefined) forEachData = this.getEmptyForEachData();

    forEachData.actions.push(action);
    this.props.updateForEachData(forEachData);
  };

  updateActionFromActions = (action: IAction, changeReason?: ESdChangeReason) => {
    let forEachData = _.cloneDeep(this.props.forEachData);
    if (forEachData === undefined) forEachData = this.getEmptyForEachData();

    forEachData.actions = forEachData?.actions.map((x) => {
      if (x.id === action.id) {
        return action;
      }
      return { ...x };
    });

    this.props.updateForEachData(forEachData, changeReason);
  };

  deleteActionFromActions = (action: IAction, changeReason?: ESdChangeReason) => {
    let forEachData = _.cloneDeep(this.props.forEachData);
    if (forEachData === undefined) {
      forEachData = this.getEmptyForEachData();
    }
    forEachData.actions = forEachData.actions.filter((x) => x.id !== action.id);
    this.props.updateForEachData(forEachData, changeReason);
  };

  reorderEventActions = (result: any, sourceIdx: number, destIdx: number) => {
    let forEachData = _.cloneDeep(this.props.forEachData);
    if (forEachData === undefined) {
      forEachData = this.getEmptyForEachData();
    }
    const [removed] = forEachData.actions.splice(sourceIdx, 1);
    forEachData.actions.splice(destIdx, 0, removed);

    this.props.updateForEachData(forEachData);
  };

  onSymbolSelect = (symbol: ISymbol, changeReason?: ESdChangeReason) => {
    let forEachData = _.cloneDeep(this.props.forEachData);
    if (forEachData === undefined) {
      forEachData = this.getEmptyForEachData();
    }
    symbol.cardinality = ECardinality.Many;
    forEachData.symbolModel = symbol;

    this.props.updateForEachData(forEachData, changeReason);
  };

  getSelectedDisplayItem = () => {
    const sd = this.props.sd.getScreenDesign();
    const selectedDisplayItem = this.props.sd.getSelectedDisplayItem(sd);
    return selectedDisplayItem;
  };

  getActionDefinition = (actionDefinitionId: string | undefined): IActionDefinitionDto | undefined => {
    if (!actionDefinitionId) return undefined;
    return this.props.sd.actionDefinitions.find((x) => x.id === actionDefinitionId);
  };

  getActionDefinitions = (actions: IAction[]): Map<string, IActionDefinitionDto> => {
    const actionDefinitions: Map<string, IActionDefinitionDto> = new Map();

    actions.forEach((action) => {
      const actionDefinition = this.getActionDefinition(action.actionDefinitionId);
      if (actionDefinition && action.actionDefinitionId) {
        actionDefinitions.set(action.actionDefinitionId, actionDefinition);
      }
    });

    return actionDefinitions;
  };

  render() {
    const actionDefinitions = this.getActionDefinitions(this.props.forEachData?.actions ?? []);
    return (
      <div
        style={{
          marginTop: 12,
          marginBottom: 12,
          backgroundColor: "var(--dark-0)"
        }}
      >
        <div key={Guid.create().toString()}>
          <span style={{ marginBottom: 6, marginTop: 8 }} className="k_Regular-G1-Left kuika_rightPanelDIName">
            <ActionParameterTitle
              parameter={{
                name: "Action Result",
                Typename: "Object",
                cardinality: ECardinality.Many
              }}
            />
          </span>

          <SymbolPicker
            action={this.props.action}
            changeType="onBlur"
            symbol={this.props.forEachData?.symbolModel}
            valueType={"Array"}
            targetCardinality={ECardinality.Many}
            onSelectedSymbol={this.onSymbolSelect}
            data_testid="actionResult"
          />
        </div>
        <div className="kuika_symbol-picker__if-else">
          <div className="kuika-right-panel__row">
            <div className="kuika-right-panel__col kuika-right-panel__col--label">
              <span
                style={{
                  paddingLeft: 6,
                  paddingTop: 10,
                  display: "block",
                  fontWeight: 700,
                  lineHeight: "normal",
                  color: "var(--dark-9)",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap"
                }}
              >
                Actions
              </span>
            </div>
            <div className="kuika-right-panel__col kuika-right-panel__col--control" style={{ textAlign: "right" }}>
              <div>
                <ActionPickerPopup
                  projectType={this.props.sd.project?.projectType}
                  options={this.state.options}
                  open={this.state.popupVisible}
                  onOpenChange={this.handlePopupOpenChange}
                  onPick={this.handlePick}
                  getActionDefinition={this.getActionDefinitionById}
                  searchTestId="forEachActionAddActionInput"
                  placeholder="Search any action"
                >
                  <span
                    data-testid="forEachAddActionButton"
                    className="kuika_addNavigationLink"
                    style={{ paddingRight: 6 }}
                  >
                    + ADD ACTION
                  </span>
                </ActionPickerPopup>
              </div>
            </div>
          </div>
          <DragDropContext
            onDragEnd={(result) => {
              if (result.destination?.index !== null && result.destination?.index !== undefined) {
                this.reorderEventActions(result, result.source.index, result.destination.index);
              }
            }}
          >
            <Droppable droppableId={"droppableForEachActions"}>
              {(provided) => (
                <div {...provided.droppableProps} ref={provided.innerRef}>
                  {this.props.forEachData?.actions.map((action, idx) => {
                    return (
                      <Draggable
                        key={`draggableForEach_${action.id}`}
                        draggableId={`draggableForEach_${action.id}`}
                        index={idx}
                      >
                        {(provided, snapshot) => (
                          <div ref={provided.innerRef} {...provided.draggableProps} {...provided.dragHandleProps}>
                            <ActionPanel
                              eventName="if"
                              onActionSaved={this.updateActionFromActions}
                              actionPanelStatus={EActionPanelStatus.edit}
                              action={_.cloneDeep(action)}
                              actionDefinition={actionDefinitions.get(action.actionDefinitionId!)}
                              deleteAction={this.deleteActionFromActions}
                              getActionDefinitionItems={this.props.getActionDefinitionItems}
                              {...provided.dragHandleProps}
                              isDragging={snapshot.isDragging}
                            />
                          </div>
                        )}
                      </Draggable>
                    );
                  })}
                </div>
              )}
            </Droppable>
          </DragDropContext>
        </div>
      </div>
    );
  }

  checkNeedRender = (changeReason: ESdChangeReason): boolean => {
    if (
      changeReason === ESdChangeReason.DiActionUpdated ||
      changeReason === ESdChangeReason.ChangeSelectedDiByCanvasFrame ||
      changeReason === ESdChangeReason.ChangeSelectedDiByTools ||
      changeReason === ESdChangeReason.WFStepMappingChange ||
      changeReason === ESdChangeReason.DragDropDi ||
      changeReason === ESdChangeReason.DragDropBlock ||
      changeReason === ESdChangeReason.ConfirmationMessageChanged ||
      changeReason === ESdChangeReason.ActionParameterFixedValueChanged ||
      changeReason === ESdChangeReason.RecurringIntervalChange
    ) {
      return true;
    }
    return false;
  };
}

const forEachActionSymbolGroup = withSD<IForEachActionSymbolGroupProps>(ForEachActionSymbolGroup);
export { forEachActionSymbolGroup as ForEachActionSymbolGroup };
